(() => {
  const SECTION_SELECTOR = '.collection-expose';

  class CollectionExposeLookController {
    constructor(section) {
      this.section = section;
      this.activeTrigger = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeyup = this.handleKeyup.bind(this);

      this.section.addEventListener('click', this.handleClick);
      document.addEventListener('keyup', this.handleKeyup);
      this.initProductRows();
    }

    get overlay() {
      return this.section.querySelector('[data-look-drawer-overlay]');
    }

    get openDrawer() {
      return this.section.querySelector(
        '.collection-expose-look-drawer.is-open[data-look-drawer]'
      );
    }

    handleClick(event) {
      const openTrigger = event.target.closest('[data-look-drawer-open]');
      if (openTrigger) {
        event.preventDefault();
        this.open(openTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-look-drawer-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target === this.overlay) {
        event.preventDefault();
        this.close();
      }
    }

    handleKeyup(event) {
      if (event.code !== 'Escape') return;
      if (!this.openDrawer) return;
      this.close();
    }

    forceClose(drawer = this.openDrawer) {
      if (!drawer) return;

      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open', 'is-closing');

      if (this.overlay) {
        this.overlay.setAttribute('aria-hidden', 'true');
        this.overlay.classList.remove('is-showing');
      }

      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeTrigger || null);
      }

      this.activeTrigger = null;
    }

    open(trigger) {
      const drawerId = trigger.getAttribute('aria-controls');
      if (!drawerId) return;

      const drawer = document.getElementById(drawerId);
      if (!drawer) return;

      if (this.openDrawer && this.openDrawer !== drawer) {
        this.forceClose(this.openDrawer);
      }

      this.activeTrigger = trigger;
      drawer.classList.remove('is-closing');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');

      if (this.overlay) {
        this.overlay.setAttribute('aria-hidden', 'false');
        this.overlay.classList.add('is-showing');
      }

      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');

      if (typeof trapFocus === 'function') {
        const focusTarget =
          drawer.querySelector('[data-look-drawer-close]') || drawer;
        trapFocus(drawer, focusTarget);
      }
    }

    close() {
      const drawer = this.openDrawer;
      if (!drawer || drawer.classList.contains('is-closing')) return;

      const panel = drawer.querySelector('.collection-expose-look-drawer__panel');
      const finishClose = () => {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-open', 'is-closing');

        if (this.overlay) {
          this.overlay.setAttribute('aria-hidden', 'true');
          this.overlay.classList.remove('is-showing');
        }

        if (this.activeTrigger) {
          this.activeTrigger.setAttribute('aria-expanded', 'false');
        }

        document.body.classList.remove('overflow-hidden');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeTrigger || null);
        }

        this.activeTrigger = null;
      };

      drawer.classList.add('is-closing');
      requestAnimationFrame(() => {
        drawer.classList.remove('is-open');

        if (this.overlay) {
          this.overlay.classList.remove('is-showing');
        }

        const prefersReducedMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const hasTransition = (() => {
          if (!panel || prefersReducedMotion) return false;
          const computed = getComputedStyle(panel);
          return computed.transitionDuration
            .split(',')
            .some(value => parseFloat(value) > 0);
        })();

        if (hasTransition) {
          panel.addEventListener('transitionend', finishClose, { once: true });
        } else {
          finishClose();
        }
      });
    }

    initProductRows() {
      window.theme?.initInlineProductRows?.(this.section);
    }
  }

  const init = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(section => {
      if (section.dataset.lookDrawerInit === 'true') return;
      section.dataset.lookDrawerInit = 'true';
      new CollectionExposeLookController(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      section.dataset.lookDrawerInit = 'false';
      init();
    }
  });
})();
